import classNames from 'classnames';
import Text from 'components/ui/Text';
import { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import debounce from 'lodash.debounce';
import { selectUiState, setUi } from 'store/uiSlice';
import { Ui } from 'type/ui';
import { useGoogleTagManager } from '@grace-studio/graceful-next/hooks';

type DependOn =
  | 'leftPanelOpen'
  | 'rightPanelOpen'
  | 'canOpenRecipe'
  | 'showOnboarding';

type AttentionGrabberType = {
  id: string;
  title: string;
  position?: DOMRect;
  reverse?: boolean;
  ref?: HTMLElement;
  delay: number;
  dependsOn?: keyof Pick<Ui, DependOn>; // Single dependency key
};

type AttentionGrabbersType = {
  tips: {
    id: string;
    text: string;
    delay: number;
    dependsOn?: Partial<Record<DependOn, boolean>>; // Object with keys from DependOn and boolean values
  }[];
  dependsOn?: Partial<Record<DependOn, boolean>>; // Object with keys from DependOn and boolean values
};

export const AttentionGrabbers: FC<AttentionGrabbersType> = ({
  tips,
  dependsOn,
}) => {
  const [elements, setElements] = useState<AttentionGrabberType[]>([]);
  const [domLoaded, setDomLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const ui = useAppSelector(selectUiState) || {};
  const clickedIds = ui?.dashboard?.attentionTips?.clickedIds || [];

  const dependOnIsActive =
    ui && dependsOn
      ? Object.entries(dependsOn).every(([key, value]) => {
          const typedKey = key as keyof Ui;

          return (
            typeof value === 'boolean' && (ui[typedKey] || false) === value
          );
        })
      : true;

  useEffect(() => {
    if (dependOnIsActive) {
      setTimeout(() => {
        setDomLoaded(true);
      }, 500);
    }
  }, [dependOnIsActive]);

  const handleClick = (id: string) => {
    dispatch(
      setUi({
        ...ui,
        tooltip: undefined,
        showOnboarding: false,
        dashboard: {
          attentionTips: {
            clickedIds: [...clickedIds, id],
          },
        },
      }),
    );
  };

  // Debounced update function to handle position recalculations
  const debouncedUpdate = debounce((id: string) => {
    handleClick(id);
  }, 300);

  useEffect(() => {
    tips.map(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return null;
      el.addEventListener('click', () => {
        debouncedUpdate(id);
      });
    });
  }, [tips]);

  useEffect(() => {
    if (domLoaded) {
      const pageWidth = document.body.clientWidth;

      const els = tips
        .map(({ id, text, delay, dependsOn }) => {
          const el = document.getElementById(id);
          if (!el) return null;

          const pos = el.getBoundingClientRect();

          return {
            id,
            title: text,
            position: pos,
            reverse: pos.left > pageWidth / 2,
            ref: el,
            dependsOn,
            delay,
          } as AttentionGrabberType;
        })
        .filter(Boolean);

      setElements(els as AttentionGrabberType[]);
    }
  }, [domLoaded]); // updateKey forces recalculation

  return (
    <>
      {elements
        .filter((el) => !clickedIds?.includes(el.id))
        .map((el, i) =>
          createPortal(
            <AttentionGrabber {...el} key={`${el.id}-${i}`} />,
            document.body,
          ),
        )}
    </>
  );
};

const AttentionGrabber: FC<AttentionGrabberType> = ({
  title,
  reverse,
  delay,
  ref,
  id,
  dependsOn,
  // position,
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);
  const [hideElements, setHideElements] = useState(false);
  const [gtmIsTriggered, setGtmIsTriggered] = useState(false);
  const initialPosition = rest.position;
  const gtm = useGoogleTagManager();
  const [position, setPosition] = useState<DOMRect | undefined>(
    ref?.getBoundingClientRect() || initialPosition,
  );
  const ui = useAppSelector(selectUiState);

  const dependOnIsActive =
    ui && dependsOn
      ? Object.entries(dependsOn).every(([key, value]) => {
          const typedKey = key as keyof Ui;

          return typeof value === 'boolean' && ui[typedKey] === value;
        })
      : true;

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoaded(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  useEffect(() => {
    setHideElements(true);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;

      const pos = el.getBoundingClientRect();
      setPosition(pos);
      setHideElements(false);
    }, 300);
  }, [loaded, ui]);

  const boundingRect = {
    top: (position?.top ?? 0) + (position?.height ?? 0) / 2,
    left: position?.left ?? 0,
    right: position?.right ?? 0,
    width: position?.width ?? 0,
  };
  const isLeftSide = boundingRect?.left < window.innerWidth / 2;

  const isHidden = hideElements || !dependOnIsActive || !loaded;

  useEffect(() => {
    if (!isHidden) {
      setGtmIsTriggered(true);
    }
  }, [isHidden]);

  useEffect(() => {
    if (gtmIsTriggered) {
      gtm.trackEvent({
        event: 'attention_tips_visible',
        eventInfo: {
          name: title,
        },
      });
    }
  }, [gtmIsTriggered]);

  return (
    <div
      className={classNames(
        ' -translate-y-1/2',
        'flex flex-row items-center justify-center fixed z-30 transition-all',
        !isLeftSide ? 'flex-row-reverse -translate-x-full ml-4' : '-ml-2',
        isHidden && 'scale-50 opacity-0',
      )}
      style={{
        top: boundingRect?.top,
        left: isLeftSide
          ? boundingRect.left + boundingRect.width
          : boundingRect.left,
      }}
    >
      <span className="relative flex h-5 w-5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
        <span className="relative inline-flex rounded-full h-5 w-5 bg-gray-400"></span>
      </span>
      <div
        className={classNames(
          'flex flex-row items-center',
          reverse ? 'flex-row-reverse -mr-[15px] ' : '-ml-[15px] ',
        )}
      >
        <div
          className={classNames(
            'w-[24px] h-[24px] flex-none bg-blue rotate-45',
            reverse ? '-translate-x-1/2' : 'translate-x-1/2',
          )}
        ></div>

        <div className="p-[24px] bg-blue rounded-md small-caps ">
          <Text
            bold
            size="label"
            elementType="span"
            color="white"
            className="!leading-none "
          >
            {title}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default AttentionGrabber;
